import { JSX, useEffect, useRef } from 'react';
import {
  Text,
  Field,
  LinkField,
  ImageField,
  Link,
  RichText,
  RichTextField,
} from '@sitecore-jss/sitecore-jss-nextjs';
import { StyleguideComponentProps } from 'lib/component-props';
import { classNames } from 'assets/utils/helper';
import { getMarginSize } from 'assets/utils/getMarginSize';
import { observeInView } from 'src/hooks/observeInView';
import VideoPlayer from 'components/molecules/VideoPlayer';

type ThreeUpProps = {
  id: string;
  fields: {
    BaseImage: ImageField;
    BaseHeading: Field<string>;
    BaseDescription: Field<string>;
    BaseLink: LinkField;
    VideoURL: Field<string>;
    BasePhoneNumber: Field<string>;
  };
};

type ThreeUpParentProps = StyleguideComponentProps & {
  fields: {
    MarginSize: {
      fields: {
        heading: {
          value: string;
        };
      };
    };
    BaseBackground: {
      value: boolean;
    };
    BaseHeadline: Field<string>;
    BaseDescription: RichTextField;
    BaseViewMoreLink: LinkField;
    children: Array<ThreeUpProps>;
    BasePhoneNumber: Field<string>;
  };
};

const ThreeUp = (props: ThreeUpParentProps): JSX.Element => {
  if (props.fields == undefined) {
    return <div>Datasource not set</div>;
  }

  const marginSize = getMarginSize(
    props?.fields?.MarginSize?.fields?.heading?.value
  );

  const animRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    if (!animRef.current) return;

    observeInView(animRef.current);
  }, []);

  useEffect(() => {
    if (!animRef.current) return;

    const wrapper = animRef.current;

    const syncCardSectionHeights = () => {
      const sections = ['card-heading', 'card-description', 'card-button'];

      sections.forEach((section) => {
        const elements = Array.from(
          wrapper.querySelectorAll<HTMLElement>(`.${section}`)
        );

        if (!elements.length) return;

        elements.forEach((element) => {
          element.style.minHeight = '';
        });

        const maxHeight = Math.max(
          ...elements.map((element) => element.offsetHeight)
        );

        elements.forEach((element) => {
          element.style.minHeight = `${maxHeight}px`;
        });
      });
    };

    const resizeObserver = new ResizeObserver(() => {
      syncCardSectionHeights();
    });

    resizeObserver.observe(wrapper);

    const images = Array.from(wrapper.querySelectorAll('img'));

    images.forEach((image) => {
      image.addEventListener('load', syncCardSectionHeights);
    });

    window.addEventListener('resize', syncCardSectionHeights);

    requestAnimationFrame(syncCardSectionHeights);

    return () => {
      resizeObserver.disconnect();

      images.forEach((image) => {
        image.removeEventListener('load', syncCardSectionHeights);
      });

      window.removeEventListener('resize', syncCardSectionHeights);
    };
  }, [props.fields.children]);

  const children = props.fields.children ?? [];

  const showImage = children.some(
    (item) => Boolean(item.fields?.BaseImage?.value?.src)
  );

  const showHeading = children.some(
    (item) => Boolean(item.fields?.BaseHeading?.value)
  );

  const showDescription = children.some(
    (item) => Boolean(item.fields?.BaseDescription?.value)
  );

  const showButton = children.some(
    (item) =>
      Boolean(item.fields?.BaseLink?.value?.href) ||
      Boolean(item.fields?.BasePhoneNumber?.value?.trim())
  );

const renderCardContent = (item: ThreeUpProps, hasLink: boolean) => {
  const hasImage = Boolean(item.fields?.BaseImage?.value?.src);
  const hasVideo = Boolean(item.fields?.VideoURL?.value);

  const phoneNumber = item.fields?.BasePhoneNumber?.value?.trim();
  const linkText = item.fields?.BaseLink?.value?.text?.trim();

  const linkLabel = phoneNumber
    ? linkText || phoneNumber
    : linkText || '';

    return (
      <div className="card-content">
        {showImage && (
          <div className={classNames('card-img', hasVideo ? 'has-video' : '')}>
            {hasImage && hasVideo ? (
              <VideoPlayer
                VideoUrl={item.fields.VideoURL}
                Thumbnail={item.fields.BaseImage}
                allowFullScreen
              />
            ) : (
              hasImage && (
                <img
                  src={item.fields?.BaseImage?.value?.src}
                  alt={
                    item.fields?.BaseImage?.value?.alt
                      ? (item.fields.BaseImage.value.alt as string)
                      : 'Image'
                  }
                />
              )
            )}
          </div>
        )}

        {showHeading && (
          <div className="card-heading">
            {item.fields?.BaseHeading?.value && (
              <Text
                tag="h3"
                className="osf-display-h3 header"
                field={item.fields.BaseHeading}
              />
            )}
          </div>
        )}

        {showDescription && (
          <div className="card-description">
            {item.fields?.BaseDescription?.value && (
              <RichText
                tag=""
                className="description osf-body-copy"
                field={item.fields.BaseDescription}
              />
            )}
          </div>
        )}

        {showButton && (
          <div className="card-button">
            {hasLink && linkLabel && (
              <button className="btn-tertiary" tabIndex={-1}>
                {linkLabel}
              </button>
            )}
          </div>
        )}
      </div>
    );
  };

  const renderVideoCardContent = (item: ThreeUpProps) => {
    const phoneNumber = item.fields?.BasePhoneNumber?.value?.trim();
    const hasBaseLink = Boolean(item.fields?.BaseLink?.value?.href);
    const hasLink = Boolean(phoneNumber || hasBaseLink);

    const linkText = item.fields?.BaseLink?.value?.text?.trim();
    const linkLabel = phoneNumber
      ? linkText || phoneNumber
      : linkText || '';

    return (
      <>
        {renderCardContent(item, false)}

        {showButton && hasLink && linkLabel && (
          <div className="card-button card-button-video">
            {phoneNumber ? (
              <a
                href={`tel:${phoneNumber}`}
                className="btn-tertiary"
              >
                {linkLabel}
              </a>
            ) : (
              <Link
                className="btn-tertiary"
                field={item.fields.BaseLink}
              />
            )}
          </div>
        )}
      </>
    );
  };

  return (
    <div
      className={classNames('overhauled', marginSize)}
      id={`three-up-${props.rendering?.uid}`}
    >
      <div
        className={`${
          props.fields.BaseBackground?.value
            ? 'bg-secondary three-up'
            : 'three-up'
        }`}
      >
        <div className="jss-inner-container">
          {props.fields.BaseHeadline?.value && (
            <div className="headline">
              <h2>{props.fields.BaseHeadline.value}</h2>
            </div>
          )}

          {props.fields.BaseDescription?.value && (
            <div className="description">
              <RichText field={props.fields.BaseDescription} />
            </div>
          )}

          <div className="jss-three-up">
            <div className="inner-wrapper anim-group" ref={animRef}>
              {props.fields?.children?.length > 0 &&
                props.fields.children.map((item, index) => {
                  const phoneNumber = item.fields?.BasePhoneNumber?.value?.trim();
                  const hasBaseLink = Boolean(item.fields?.BaseLink?.value?.href);
                  const hasLink = Boolean(phoneNumber || hasBaseLink);
                  const hasVideo = Boolean(item.fields?.VideoURL?.value);

                  return (
                    <div
                      key={item.id}
                      className="groupings-anim animated"
                      style={{ '--index': index + 1 } as React.CSSProperties}
                    >
                      {hasLink && !hasVideo ? (
                      phoneNumber ? (
                        <a
                          href={`tel:${phoneNumber}`}
                          className="link-card base-card inner-side-spacing"
                        >
                          {renderCardContent(item, hasLink)}
                        </a>
                      ) : (
                        <Link
                          field={item.fields.BaseLink}
                          className="link-card base-card inner-side-spacing"
                        >
                          {renderCardContent(item, hasLink)}
                        </Link>
                      )
                    ) : (
                        <div
                          className={classNames(
                            'base-card inner-side-spacing',
                            hasVideo ? 'video-card' : ''
                          )}
                        >
                          {hasVideo
                            ? renderVideoCardContent(item)
                            : renderCardContent(item, hasLink)}
                        </div>
                      )}
                    </div>
                  );
                })}
            </div>

            {(props.fields?.BasePhoneNumber?.value?.trim() ||
              props.fields?.BaseViewMoreLink?.value?.href) && (
              <div className="view-more">
                {props.fields?.BasePhoneNumber?.value?.trim() ? (
                  <a
                    href={`tel:${props.fields.BasePhoneNumber.value.trim()}`}
                    className="btn-primary"
                  >
                    {props.fields?.BaseViewMoreLink?.value?.text?.trim() ||
                      props.fields.BasePhoneNumber.value.trim()}
                  </a>
                ) : (
                  <Link
                    className="btn-primary"
                    field={props.fields.BaseViewMoreLink}
                  />
                )}

                <div className="divider"></div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default ThreeUp;